import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Pencil, Newspaper, Eye, EyeOff } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import type { BlogPost } from '../../types';
import { formatDate } from '../../lib/format';
import { useUpload } from '../../lib/useUpload';
import {
  Spinner, EmptyState, Modal, Alert, ConfirmDialog, UploadProgress,
  inputClass, labelClass, primaryBtn, secondaryBtn,
} from '../../components/ui';

interface PostForm {
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  author_name: string;
  is_published: boolean;
  cover_image: File | null;
}

const emptyForm = (): PostForm => ({
  title: '',
  slug: '',
  excerpt: '',
  content: '',
  author_name: '',
  is_published: true,
  cover_image: null,
});

export default function Blog() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [modal, setModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<PostForm>(emptyForm());
  const upload = useUpload();
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const load = () => {
    setLoading(true);
    api.get<BlogPost[]>('/blog/admin/all').then(setPosts).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const openAdd = () => {
    setError('');
    setEditingId(null);
    setForm(emptyForm());
    setModal(true);
  };

  const openEdit = (p: BlogPost) => {
    setError('');
    setEditingId(p.id);
    setForm({
      title: p.title,
      slug: p.slug,
      excerpt: p.excerpt || '',
      content: p.content || '',
      author_name: p.author_name || '',
      is_published: p.is_published,
      cover_image: null,
    });
    setModal(true);
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    upload.reset();
    setError('');
    try {
      const fd = new FormData();
      fd.append('title', form.title);
      fd.append('slug', form.slug);
      fd.append('excerpt', form.excerpt);
      fd.append('content', form.content);
      fd.append('author_name', form.author_name);
      fd.append('is_published', form.is_published ? '1' : '0');
      if (form.cover_image) fd.append('image', form.cover_image);
      if (editingId) {
        await upload.run(`/blog/${editingId}`, fd, 'PUT');
      } else {
        await upload.run('/blog', fd);
      }
      setModal(false);
      load();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const remove = async (id: number) => {
    try {
      await api.delete(`/blog/${id}`);
      setDeleteId(null);
      load();
    } catch {
      setDeleteId(null);
    }
  };

  if (loading) return <Spinner label="Loading blog posts..." />;

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Blog</h1>

      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <h2 className="text-lg font-semibold">Posts</h2>
        <button onClick={openAdd} className={primaryBtn}>
          <Plus className="h-4 w-4" /> New Post
        </button>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Posts are shown on the public Blog page. Drafts (unpublished) are hidden from visitors.
      </p>

      {error && <div className="mb-4"><Alert>{error}</Alert></div>}

      {posts.length === 0 ? (
        <EmptyState message="No blog posts yet. Click 'New Post' to write the first one." />
      ) : (
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Post</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Author</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Date</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {posts.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        {p.cover_image ? (
                          <img src={p.cover_image} alt="" className="h-12 w-16 object-cover rounded-lg mr-3 shrink-0" />
                        ) : (
                          <div className="h-12 w-16 bg-gray-100 rounded-lg mr-3 shrink-0 flex items-center justify-center">
                            <Newspaper className="h-5 w-5 text-gray-300" />
                          </div>
                        )}
                        <div>
                          <p className="font-semibold text-gray-800">{p.title}</p>
                          <Link to={`/blog/${p.slug}`} className="text-xs text-primary hover:underline">/{p.slug}</Link>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{p.author_name || '—'}</td>
                    <td className="px-4 py-3">
                      {p.is_published ? (
                        <span className="inline-flex items-center text-xs font-semibold px-2 py-1 rounded-full bg-green-100 text-green-800">
                          <Eye className="h-3.5 w-3.5 mr-1" /> Published
                        </span>
                      ) : (
                        <span className="inline-flex items-center text-xs font-semibold px-2 py-1 rounded-full bg-amber-100 text-amber-800">
                          <EyeOff className="h-3.5 w-3.5 mr-1" /> Draft
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatDate(p.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end space-x-2">
                        <button onClick={() => openEdit(p)} className="p-2 rounded-lg hover:bg-primary/10 text-primary" title="Edit">
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button onClick={() => setDeleteId(p.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500" title="Delete">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Modal open={modal} onClose={() => setModal(false)} title={editingId ? 'Edit Post' : 'New Post'}>
        <form onSubmit={save} className="space-y-4">
          <div>
            <label className={labelClass}>Title *</label>
            <input
              className={inputClass}
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Inside Our Latest Lagos Estate"
            />
          </div>
          <div>
            <label className={labelClass}>Slug (URL) - optional</label>
            <input
              className={inputClass}
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              placeholder="auto-generated from the title"
            />
            <p className="text-xs text-gray-400 mt-1">Lowercase letters, numbers and dashes only.</p>
          </div>
          <div>
            <label className={labelClass}>Excerpt / Short Summary</label>
            <textarea
              className={inputClass}
              rows={2}
              value={form.excerpt}
              onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
              placeholder="A one or two sentence summary shown on the blog list."
            ></textarea>
          </div>
          <div>
            <label className={labelClass}>Content</label>
            <textarea
              className={inputClass}
              rows={10}
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              placeholder="Write the full post here. Paragraphs and line breaks are preserved."
            ></textarea>
          </div>
          <div>
            <label className={labelClass}>Author Name (optional)</label>
            <input
              className={inputClass}
              value={form.author_name}
              onChange={(e) => setForm({ ...form, author_name: e.target.value })}
              placeholder="Leave blank to use your account name, or type a custom name for anonymous posting."
            />
            <p className="text-xs text-gray-400 mt-1">If left empty, your account name is used. Set a custom name or leave as-is for anonymous-style byline.</p>
          </div>
          <div>
            <label className={labelClass}>Cover Image</label>
            <input
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              className={inputClass}
              onChange={(e) => setForm({ ...form, cover_image: e.target.files?.[0] || null })}
            />
          </div>
          <label className="flex items-center space-x-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={form.is_published}
              onChange={(e) => setForm({ ...form, is_published: e.target.checked })}
              className="h-4 w-4 rounded border-gray-300"
            />
            <span>Publish (visible on the public Blog page)</span>
          </label>
          <UploadProgress
            percent={upload.percent}
            phase={upload.phase}
            error={upload.error}
            onCancel={upload.cancel}
          />
          <div className="flex justify-end space-x-3">
            <button type="button" onClick={() => setModal(false)} className={secondaryBtn}>Cancel</button>
            <button type="submit" disabled={upload.busy} className={primaryBtn}>
              {upload.phase === 'uploading'
                ? `Uploading ${upload.percent}%`
                : upload.phase === 'processing' ? 'Saving...' : editingId ? 'Save Changes' : 'Create Post'}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={() => deleteId && remove(deleteId)}
        title="Delete Post"
        message="Are you sure you want to delete this blog post? This cannot be undone."
      />
    </div>
  );
}
